import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast, { Toaster } from "react-hot-toast";
import ApplyLeaveModal from "./ApplyLeaveModal";
import WarningModal from "./WarningModal";
import { fetchLeaves, applyLeave, updateLeave, deleteLeave } from "../../redux/applyLeaveSlice";
import { fetchAssignedLeavesByEmail } from "../../redux/assignLeaveSlice";
import { fetchAttendanceByEmail, addAttendance, updateAttendance, addBreak, endBreak, checkAttendanceStatus } from "../../redux/attendanceSlice";
import { getSessionsByTeacherEmail } from "../../redux/sessionSlice";
import { useDispatch, useSelector } from "react-redux";
import { X } from "lucide-react";
import { fetchHolidaysForDate } from "../../redux/holidaySlice";
import GoalPlanningModal from "../admin/GoalPlanningModal";
import AdminCheckInModal from "../admin/AdminCheckInModal";
import SiteVisitModal from "../admin/SiteVisitModal";
import {
  fetchActiveGoalPlanning,
  fetchGoalPlanningsByEmail,
  saveAdminDraftGoalPlanning,
  submitAdminGoalPlanning,
} from "../../redux/goalPlanningSlice";
import {
  fetchActiveAdminCheckIn,
  fetchAdminCheckInsByEmail,
  saveAdminDraftCheckIn,
  submitAdminCheckIn,
} from "../../redux/adminCheckInSlice";
import {
  fetchActiveSiteVisit,
  fetchSiteVisitsByEmail,
  saveAdminDraftSiteVisit,
  submitAdminSiteVisit,
} from "../../redux/siteVisitSlice";

const AttendanceModal = ({ isOpen, onClose, onSubmit, handleSubmit, register, reset }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white w-11/12 md:w-2/3 lg:w-1/3 overflow-hidden rounded shadow-lg relative">
        <div className="flex justify-between items-center border-b pb-2 px-4">
          <h2 className="text-xl font-semibold">Mark Attendance</h2>
          <button className="p-2 hover:bg-gray-200 rounded-full" onClick={() => { onClose(); reset(); }}>
            <X />
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-4">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-4">
              <label className="block text-gray-700 font-medium">Status</label>
              <select {...register("status", { required: true })} className="mt-2 w-full border rounded p-2">
                <option value="">Select Status</option>
                <option value="Present">Present</option>
                <option value="Late">Late</option>
              </select>
            </div>
            <div className="flex justify-end gap-4">
              <button type="button" className="bg-gray-300 text-gray-700 px-4 py-2 rounded" onClick={() => { onClose(); reset(); }}>
                Cancel
              </button>
              <button type="submit" className="bg-[#241763] text-white px-4 py-2 rounded">
                Mark Attendance
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const AdminDashboardModal = ({ isOpen, onClose, adminEmail, adminName = "" }) => {
  const dispatch = useDispatch();
  const { register, handleSubmit, reset, setValue } = useForm();
  const { leaves, status, error } = useSelector((state) => state.leaves);
  const { assignedLeaves, status: assignStatus, error: assignError } = useSelector((state) => state.assignLeave);
  const { attendanceRecords, status: attendanceStatus, error: attendanceError } = useSelector((state) => state.attendance);
  const { teacherSessions, status: sessionsStatus } = useSelector((state) => state.sessions);
  const { holidaysForDate } = useSelector((state) => state.holidays);
  const { activeGoalPlanning, goalPlannings, loading: goalPlanningLoading } = useSelector(
    (state) =>
      state.goalPlanning || {
        activeGoalPlanning: null,
        goalPlannings: [],
        loading: false,
      }
  );
  const {
    activeMidYearCheckIn,
    activeEndOfYearCheckIn,
    adminCheckIns,
    loading: checkInLoading,
  } = useSelector(
    (state) =>
      state.adminCheckIn || {
        activeMidYearCheckIn: null,
        activeEndOfYearCheckIn: null,
        adminCheckIns: [],
        loading: false,
      }
  );
  const { activeSiteVisit, siteVisits, loading: siteVisitLoading } = useSelector(
    (state) =>
      state.siteVisit || {
        activeSiteVisit: null,
        siteVisits: [],
        loading: false,
      }
  );

  const [isApplyLeaveModalOpen, setIsApplyLeaveModalOpen] = useState(false);
  const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [isGoalPlanningModalOpen, setIsGoalPlanningModalOpen] = useState(false);
  const [goalPlanningModalMode, setGoalPlanningModalMode] = useState("admin");
  const [selectedGoalPlanning, setSelectedGoalPlanning] = useState(null);
  const [isCheckInModalOpen, setIsCheckInModalOpen] = useState(false);
  const [checkInModalMode, setCheckInModalMode] = useState("admin");
  const [selectedCheckIn, setSelectedCheckIn] = useState(null);
  const [selectedCheckInType, setSelectedCheckInType] = useState("mid_year");
  const [isSiteVisitModalOpen, setIsSiteVisitModalOpen] = useState(false);
  const [siteVisitModalMode, setSiteVisitModalMode] = useState("admin");
  const [selectedSiteVisit, setSelectedSiteVisit] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [currentLeave, setCurrentLeave] = useState(null);
  const [isMultipleDays, setIsMultipleDays] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (isOpen && adminEmail) {
      // Refresh all data when modal opens
      dispatch(fetchLeaves({ adminEmail }));
      dispatch(fetchAssignedLeavesByEmail(adminEmail));
      dispatch(fetchAttendanceByEmail(adminEmail));
      dispatch(getSessionsByTeacherEmail(adminEmail));
      dispatch(fetchHolidaysForDate(today));
      dispatch(fetchActiveGoalPlanning(adminEmail));
      dispatch(fetchGoalPlanningsByEmail(adminEmail));
      dispatch(fetchAdminCheckInsByEmail(adminEmail));
      dispatch(fetchActiveAdminCheckIn({ adminEmail, checkInType: "mid_year" }));
      dispatch(fetchActiveAdminCheckIn({ adminEmail, checkInType: "end_of_year" }));
      dispatch(fetchSiteVisitsByEmail(adminEmail));
      dispatch(fetchActiveSiteVisit(adminEmail));
    }
  }, [isOpen, adminEmail, dispatch, today]);

  const refreshGoalPlanning = () => {
    if (adminEmail) {
      dispatch(fetchActiveGoalPlanning(adminEmail));
      dispatch(fetchGoalPlanningsByEmail(adminEmail));
    }
  };

  const refreshCheckIns = () => {
    if (adminEmail) {
      dispatch(fetchAdminCheckInsByEmail(adminEmail));
      dispatch(fetchActiveAdminCheckIn({ adminEmail, checkInType: "mid_year" }));
      dispatch(fetchActiveAdminCheckIn({ adminEmail, checkInType: "end_of_year" }));
    }
  };

  const refreshSiteVisits = () => {
    if (adminEmail) {
      dispatch(fetchSiteVisitsByEmail(adminEmail));
      dispatch(fetchActiveSiteVisit(adminEmail));
    }
  };

  const getGoalPlanningStatusLabel = (status, reinitiatedAt = null) => {
    if (status === "initiated" && reinitiatedAt) return "Reinitiated — Pending Your Update";
    if (status === "initiated") return "Pending";
    if (status === "submitted_by_admin") return "Submitted - Awaiting Conference";
    if (status === "completed") return "Completed";
    return status;
  };

  const getSiteVisitStatusLabel = (status) => {
    if (status === "initiated") return "Pre-Observation Available";
    if (status === "observations_captured") return "Post-Observation Unlocked";
    if (status === "submitted_by_admin") return "Awaiting Evaluation";
    if (status === "completed") return "Completed (Locked)";
    return status;
  };

  const getSiteVisitStatusClass = (status) => {
    if (status === "initiated") return "bg-yellow-100 text-yellow-800";
    if (status === "observations_captured") return "bg-orange-100 text-orange-800";
    if (status === "submitted_by_admin") return "bg-blue-100 text-blue-800";
    if (status === "completed") return "bg-green-100 text-green-800";
    return "bg-gray-100 text-gray-800";
  };

  const getGoalPlanningStatusClass = (status) => {
    if (status === "initiated") return "bg-yellow-100 text-yellow-800";
    if (status === "submitted_by_admin") return "bg-blue-100 text-blue-800";
    if (status === "completed") return "bg-green-100 text-green-800";
    return "bg-gray-100 text-gray-800";
  };

  const handleOpenGoalPlanningForm = () => {
    setGoalPlanningModalMode("admin");
    setSelectedGoalPlanning(null);
    setIsGoalPlanningModalOpen(true);
  };

  const handleViewGoalPlanning = (goalPlanning) => {
    setGoalPlanningModalMode("view");
    setSelectedGoalPlanning(goalPlanning);
    setIsGoalPlanningModalOpen(true);
  };

  const handleCloseGoalPlanningModal = () => {
    setIsGoalPlanningModalOpen(false);
    setSelectedGoalPlanning(null);
    setGoalPlanningModalMode("admin");
  };

  const handleAdminGoalPlanningDraftSave = async (formData) => {
    if (!activeGoalPlanning?.id || !adminEmail) return;

    try {
      await dispatch(
        saveAdminDraftGoalPlanning({
          id: activeGoalPlanning.id,
          adminEmail,
          formData,
        })
      ).unwrap();
      setIsGoalPlanningModalOpen(false);
      refreshGoalPlanning();
    } catch (err) {
      toast.error(err || "Failed to save goal planning draft");
    }
  };

  const handleAdminGoalPlanningSubmit = async (formData) => {
    if (!activeGoalPlanning?.id || !adminEmail) return;

    try {
      await dispatch(
        submitAdminGoalPlanning({
          id: activeGoalPlanning.id,
          adminEmail,
          formData,
        })
      ).unwrap();
      setIsGoalPlanningModalOpen(false);
      refreshGoalPlanning();
    } catch (err) {
      toast.error(err || "Failed to submit goal planning");
    }
  };

  const getCheckInLabel = (type) =>
    type === "mid_year" ? "Mid-Year Check-in" : "End-of-Year Reflection";

  const handleOpenCheckInForm = (checkInType) => {
    setSelectedCheckInType(checkInType);
    setCheckInModalMode("admin");
    setSelectedCheckIn(null);
    setIsCheckInModalOpen(true);
  };

  const handleViewCheckIn = (checkIn) => {
    setSelectedCheckIn(checkIn);
    setSelectedCheckInType(checkIn.checkInType);
    setCheckInModalMode("view");
    setIsCheckInModalOpen(true);
  };

  const handleCloseCheckInModal = () => {
    setIsCheckInModalOpen(false);
    setSelectedCheckIn(null);
    setCheckInModalMode("admin");
  };

  const handleAdminCheckInDraftSave = async (formData) => {
    const activeCheckIn =
      selectedCheckInType === "mid_year"
        ? activeMidYearCheckIn
        : activeEndOfYearCheckIn;

    if (!activeCheckIn?.id || !adminEmail) return;

    try {
      await dispatch(
        saveAdminDraftCheckIn({
          id: activeCheckIn.id,
          adminEmail,
          formData,
        })
      ).unwrap();
      setIsCheckInModalOpen(false);
      refreshCheckIns();
    } catch (err) {
      toast.error(err || "Failed to save check-in draft");
    }
  };

  const handleAdminCheckInSubmit = async (formData) => {
    const activeCheckIn =
      selectedCheckInType === "mid_year"
        ? activeMidYearCheckIn
        : activeEndOfYearCheckIn;

    if (!activeCheckIn?.id || !adminEmail) return;

    try {
      await dispatch(
        submitAdminCheckIn({
          id: activeCheckIn.id,
          adminEmail,
          formData,
        })
      ).unwrap();
      setIsCheckInModalOpen(false);
      refreshCheckIns();
    } catch (err) {
      toast.error(err || "Failed to submit check-in");
    }
  };

  const handleOpenSiteVisitForm = (siteVisit = null) => {
    setSiteVisitModalMode("admin");
    setSelectedSiteVisit(siteVisit || activeSiteVisit || null);
    setIsSiteVisitModalOpen(true);
  };

  const handleViewSiteVisit = (siteVisit) => {
    setSelectedSiteVisit(siteVisit);
    setSiteVisitModalMode("view");
    setIsSiteVisitModalOpen(true);
  };

  const handleCloseSiteVisitModal = () => {
    setIsSiteVisitModalOpen(false);
    setSelectedSiteVisit(null);
    setSiteVisitModalMode("admin");
  };

  const handleAdminSiteVisitSubmit = async (formData) => {
    const siteVisitId = selectedSiteVisit?.id || activeSiteVisit?.id
    if (!siteVisitId || !adminEmail) return;

    try {
      await dispatch(
        submitAdminSiteVisit({
          id: siteVisitId,
          adminEmail,
          formData,
        })
      ).unwrap();
      setIsSiteVisitModalOpen(false);
      refreshSiteVisits();
    } catch (err) {
      toast.error(err || "Failed to submit site visit");
    }
  };

  const handleAdminSiteVisitDraftSave = async (formData) => {
    const siteVisitId = selectedSiteVisit?.id || activeSiteVisit?.id
    if (!siteVisitId || !adminEmail) return;

    try {
      await dispatch(
        saveAdminDraftSiteVisit({
          id: siteVisitId,
          adminEmail,
          formData,
        })
      ).unwrap();
      setIsSiteVisitModalOpen(false);
      refreshSiteVisits();
    } catch (err) {
      toast.error(err || "Failed to save site visit draft");
    }
  };

  // Refresh data after leave operations
  const refreshData = () => {
    if (adminEmail) {
      dispatch(fetchLeaves({ adminEmail }));
      dispatch(fetchAssignedLeavesByEmail(adminEmail));
      dispatch(fetchAttendanceByEmail(adminEmail));
    }
  };

  const todayAttendance = attendanceRecords.find(
    (record) => record.date === today && (record.teacherEmail === adminEmail || record.adminEmail === adminEmail)
  );

  const isHoliday = Array.isArray(holidaysForDate) && holidaysForDate.length > 0;

  const calculateUsedLeaves = (leaveType) => {
    return leaves
      .filter((leave) => (leave.adminEmail === adminEmail || leave.teacherEmail === adminEmail) && leave.leaveType === leaveType)
      .reduce((total, leave) => {
        const dateFrom = new Date(leave.dateFrom);
        const dateTo = leave.dateTo ? new Date(leave.dateTo) : dateFrom;
        const diffTime = Math.abs(dateTo.getTime() - dateFrom.getTime());
        return total + Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      }, 0);
  };

  // Helper function to get emoji based on leave type
  const getLeaveTypeEmoji = (leaveType) => {
    const type = leaveType.toLowerCase();
    if (type.includes('annual') || type.includes('annual leave')) return "🎈";
    if (type.includes('sick') || type.includes('sick leave')) return "🩺";
    if (type.includes('casual') || type.includes('casual leave')) return "🎉";
    if (type.includes('maternity')) return "👶";
    if (type.includes('paternity')) return "👨‍👩‍👧‍👦";
    if (type.includes('hajj') || type.includes('ziarat')) return "🕋";
    return "📅"; // Default emoji
  };

  // Dynamic leave summary based on assigned leaves
  const leaveSummary = assignedLeaves.map((assigned) => {
    const usedLeaves = calculateUsedLeaves(assigned.leaveType);
    const approvedLeaves = leaves
      .filter((leave) => (leave.adminEmail === adminEmail || leave.teacherEmail === adminEmail) && 
                         leave.leaveType === assigned.leaveType && 
                         leave.status === "Approved")
      .reduce((total, leave) => {
        const dateFrom = new Date(leave.dateFrom);
        const dateTo = leave.dateTo ? new Date(leave.dateTo) : dateFrom;
        const diffTime = Math.abs(dateTo.getTime() - dateFrom.getTime());
        return total + Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      }, 0);
    
    // Calculate original total by adding back the approved leaves to current balance
    const originalTotal = assigned.numberOfLeave + approvedLeaves;
    
    return {
      title: assigned.leaveType,
      leaveType: assigned.leaveType,
      emoji: getLeaveTypeEmoji(assigned.leaveType),
      total: originalTotal,
      used: usedLeaves,
    };
  });

  // Dynamic total assigned leaves (original totals)
  const totalAssignedLeaves = assignedLeaves.reduce((acc, leave) => {
    const approvedLeaves = leaves
      .filter((l) => (l.adminEmail === adminEmail || l.teacherEmail === adminEmail) && 
                     l.leaveType === leave.leaveType && 
                     l.status === "Approved")
      .reduce((total, l) => {
        const dateFrom = new Date(l.dateFrom);
        const dateTo = l.dateTo ? new Date(l.dateTo) : dateFrom;
        const diffTime = Math.abs(dateTo.getTime() - dateFrom.getTime());
        return total + Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      }, 0);
    
    // Calculate original total by adding back the approved leaves to current balance
    acc[leave.leaveType] = leave.numberOfLeave + approvedLeaves;
    return acc;
  }, {});

  const attendanceSummary = {
    present: attendanceRecords.filter((r) => (r.teacherEmail === adminEmail || r.adminEmail === adminEmail) && r.status === "Present").length,
    late: attendanceRecords.filter((r) => (r.teacherEmail === adminEmail || r.adminEmail === adminEmail) && r.status === "Late").length,
    onLeave: attendanceRecords.filter((r) => (r.teacherEmail === adminEmail || r.adminEmail === adminEmail) && r.status === "On Leave").length,
    absent: attendanceRecords.filter((r) => (r.teacherEmail === adminEmail || r.adminEmail === adminEmail) && r.status === "Absent").length,
  };

  // Enhanced stats for better display
  const totalAttendanceDays = attendanceRecords.filter((r) => r.teacherEmail === adminEmail || r.adminEmail === adminEmail).length;
  const totalLeaveDays = leaveSummary.reduce((sum, item) => sum + item.used, 0);
  const totalAssignedLeaveDays = leaveSummary.reduce((sum, item) => sum + item.total, 0);
  const remainingLeaveDays = totalAssignedLeaveDays - totalLeaveDays;

  const onApplyLeaveSubmit = async (data) => {
    try {
      data.adminEmail = adminEmail;
      if (isEdit) {
        await dispatch(updateLeave({ id: currentLeave.id, updatedData: data })).unwrap();
        toast.success("Leave updated successfully!");
      } else {
        await dispatch(applyLeave(data)).unwrap();
        toast.success("Leave applied successfully!");
      }
      setIsApplyLeaveModalOpen(false);
      reset();
      refreshData(); // Refresh data after leave operation
    } catch (err) {
      toast.error(err.message || "Error processing request.");
    }
  };

  const onMarkAttendanceSubmit = async (data) => {
    try {
      const today = new Date().toISOString().split("T")[0];
      
      // First check if attendance can be marked
      const statusCheck = await dispatch(checkAttendanceStatus({ 
        email: adminEmail, 
        date: today 
      })).unwrap();
      
      if (isHoliday) {
        toast.error("Today is a holiday. Attendance cannot be marked.");
        return;
      }

      if (!statusCheck.canMarkAttendance) {
        toast.error(statusCheck.reason);
        return;
      }
      
      const attendanceData = {
        adminEmail,
        date: today,
        status: data.status,
      };
      await dispatch(addAttendance(attendanceData)).unwrap();
      toast.success("Attendance marked successfully!");
      setIsAttendanceModalOpen(false);
      refreshData(); // Refresh data after attendance marking
      reset();
    } catch (err) {
      toast.error(err.message || "Error marking attendance.");
    }
  };

  const handleCheckOut = async () => {
    if (!todayAttendance) {
      toast.error("No check-in record found for today.");
      return;
    }
    if (todayAttendance.checkOut) {
      toast.error("Check-out already recorded for today.");
      return;
    }
    try {
      const currentTime = new Date().toLocaleTimeString("en-US", { hour12: false });
      await dispatch(updateAttendance({
        id: todayAttendance.id,
        updatedData: { checkOut: currentTime },
      })).unwrap();
      toast.success("Check-out recorded successfully!");
      refreshData(); // Refresh data after check-out
    } catch (err) {
      toast.error(err.message || "Error recording check-out.");
    }
  };

  const handleBreakIn = async () => {
    if (!todayAttendance) {
      toast.error("No check-in record found for today.");
      return;
    }
    try {
      await dispatch(addBreak({ id: todayAttendance.id }));
      toast.success("Break started successfully!");
      refreshData(); // Refresh data after break start
    } catch (err) {
      toast.error(err.message || "Error starting break.");
    }
  };

  const handleBreakOut = async () => {
    if (!todayAttendance) {
      toast.error("No check-in record found for today.");
      return;
    }
    const breaks = todayAttendance.breaks ? JSON.parse(todayAttendance.breaks) : [];
    if (!breaks.length || breaks[breaks.length - 1].breakOut) {
      toast.error("No active break to end.");
      return;
    }
    try {
      await dispatch(endBreak({ id: todayAttendance.id }));
      toast.success("Break ended successfully!");
      refreshData(); // Refresh data after break end
    } catch (err) {
      toast.error(err.message || "Error ending break.");
    }
  };

  const handleEditLeave = (leave) => {
    setIsEdit(true);
    setCurrentLeave(leave);
    setIsApplyLeaveModalOpen(true);
    setValue("leaveType", leave.leaveType);
    setValue("dateFrom", leave.dateFrom ? leave.dateFrom.split("T")[0] : "");
    const dateFrom = new Date(leave.dateFrom);
    const dateTo = leave.dateTo ? new Date(leave.dateTo) : null;
    const isOneDay = !dateTo || dateFrom.toDateString() === (dateTo ? dateTo.toDateString() : dateFrom.toDateString());
    setIsMultipleDays(!isOneDay);
    setValue("dateTo", leave.dateTo ? leave.dateTo.split("T")[0] : "");
    setValue("reason", leave.reason);
    setValue("status", leave.status);
  };

  const handleDeleteLeave = async (id) => {
    if (window.confirm("Are you sure you want to delete this leave?")) {
      await dispatch(deleteLeave(id)).unwrap();
      toast.success("Leave deleted successfully!");
      refreshData(); // Refresh data after delete operation
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-4xl relative-va relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 text-lg">
          ✖
        </button>
        <div className="text-sm">
          <h2 className="text-lg font-semibold mb-4">Admin Dashboard</h2>
          {isHoliday && (
            <div className="mb-4 bg-purple-50 border border-purple-200 text-purple-800 px-4 py-3 rounded-lg">
              <div className="font-semibold flex items-center gap-2">
                <span role="img" aria-label="celebration">🎉</span>
                Today is a public holiday
              </div>
              <div className="mt-1 text-sm">
                {holidaysForDate.map((holiday) => holiday.name).join(", ")}
              </div>
            </div>
          )}
          <div className="grid grid-cols-5 gap-4 text-center bg-gray-100 p-4 rounded-lg mb-4">
            {[
              { icon: "📅", label: "Total Days", value: totalAttendanceDays },
              { icon: "🧑‍💼", label: "Present", value: attendanceSummary.present },
              { icon: "⏰", label: "Late", value: attendanceSummary.late },
              { icon: "📄", label: "On Leave", value: attendanceSummary.onLeave },
              { icon: "🚩", label: "Absent", value: attendanceSummary.absent },
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center">
                <span className="text-2xl">{item.icon}</span>
                <span className="text-lg font-semibold">{item.value}</span>
                <span className="text-gray-500">{item.label}</span>
              </div>
            ))}
          </div>
          
          {/* Enhanced Leave Stats */}
          <div className="grid grid-cols-4 gap-4 text-center bg-blue-50 p-4 rounded-lg mb-4">
            {[
              { icon: "📋", label: "Total Assigned", value: totalAssignedLeaveDays },
              { icon: "✅", label: "Used Leave", value: totalLeaveDays },
              { icon: "⏳", label: "Remaining", value: remainingLeaveDays },
              { icon: "📝", label: "Leave Applications", value: leaves.filter((leave) => leave.adminEmail === adminEmail || leave.teacherEmail === adminEmail).length },
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center">
                <span className="text-2xl">{item.icon}</span>
                <span className="text-lg font-semibold">{item.value}</span>
                <span className="text-gray-500">{item.label}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
            {[
              { 
                title: "Mark Attendance", 
                button: isHoliday ? "Holiday" : todayAttendance ? "Already Marked" : "Mark Now", 
                action: () => setIsAttendanceModalOpen(true), 
                disabled: !!todayAttendance || isHoliday 
              },
              { 
                title: "Check Out", 
                button: "Check Out", 
                action: handleCheckOut, 
                disabled: !todayAttendance || !todayAttendance.checkIn || todayAttendance.checkOut 
              },
              { 
                title: "Go Away/Break In", 
                button: "Start Break", 
                action: handleBreakIn, 
                disabled: !todayAttendance || !todayAttendance.checkIn || todayAttendance.checkOut 
              },
              { 
                title: "Back/Break Out", 
                button: "End Break", 
                action: handleBreakOut, 
                disabled: !todayAttendance || !todayAttendance.checkIn || todayAttendance.checkOut 
              },
              { title: "My Profile", button: "View" },
              { title: "Apply Leave", button: "Apply Now", action: () => setIsApplyLeaveModalOpen(true) },
              { title: "Warnings", button: "View", action: () => setIsWarningModalOpen(true) },
              ...(activeGoalPlanning
                ? [
                    {
                      title: "Goal Planning",
                      button: activeGoalPlanning.reinitiatedAt
                        ? "Update Reinitiated Form"
                        : "Open Goal Planning Form",
                      action: handleOpenGoalPlanningForm,
                      disabled: false,
                      highlight: true,
                      description: activeGoalPlanning.reinitiatedAt
                        ? "This form was reinitiated by Super Admin. Please review, update, and resubmit."
                        : "Complete your annual goals form. Conference section will be filled by Super Admin.",
                    },
                  ]
                : []),
              ...(activeMidYearCheckIn
                ? [
                    {
                      title: "Mid-Year Check-in",
                      button: activeMidYearCheckIn.reinitiatedAt
                        ? "Update Reinitiated Form"
                        : "Open Mid-Year Check-in",
                      action: () => handleOpenCheckInForm("mid_year"),
                      disabled: false,
                      highlight: true,
                      highlightColor: "blue",
                      description: activeMidYearCheckIn.reinitiatedAt
                        ? "This form was reinitiated by Super Admin. Please review, update, and resubmit."
                        : "Complete your Mid-Year Check-in. Conference section will be filled by Super Admin.",
                    },
                  ]
                : []),
              ...(activeEndOfYearCheckIn
                ? [
                    {
                      title: "End-of-Year Reflection",
                      button: activeEndOfYearCheckIn.reinitiatedAt
                        ? "Update Reinitiated Form"
                        : "Open End-of-Year Form",
                      action: () => handleOpenCheckInForm("end_of_year"),
                      disabled: false,
                      highlight: true,
                      highlightColor: "blue",
                      description: activeEndOfYearCheckIn.reinitiatedAt
                        ? "This form was reinitiated by Super Admin. Please review, update, and resubmit."
                        : "Complete your End-of-Year Reflection. Conference section will be filled by Super Admin.",
                    },
                  ]
                : []),
              ...(activeSiteVisit
                ? [
                    {
                      title: "Observation/Site Visit",
                      button:
                        activeSiteVisit.status === "observations_captured"
                          ? "Complete Post-Observation"
                          : "Complete Pre-Observation",
                      action: handleOpenSiteVisitForm,
                      disabled: false,
                      highlight: true,
                      highlightColor: "blue",
                      description:
                        activeSiteVisit.status === "observations_captured"
                          ? "Observation evidence is ready. Complete Post-Observation and submit."
                          : "Complete Pre-Observation. Post-Observation unlocks after evaluator evidence.",
                    },
                  ]
                : []),
            ].map((item, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg shadow-md text-center ${
                  item.highlight
                    ? item.highlightColor === "blue"
                      ? "bg-[#D9EAF7] border border-[#5B9BD5]"
                      : "bg-[#FDE9D9] border border-[#ED7D31]"
                    : "bg-gray-100"
                }`}
              >
                <h3 className="text-md font-semibold">{item.title}</h3>
                {item.highlight && (
                  <p className="text-xs text-gray-600 mt-1 mb-2">
                    {item.description ||
                      "Complete your annual goals form. Conference section will be filled by Super Admin."}
                  </p>
                )}
                {item.description?.includes("reinitiated") && (
                  <span className="inline-block mb-2 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#FDE9D9] text-[#C65911]">
                    Reinitiated
                  </span>
                )}
                <button
                  className={`mt-2 px-4 py-2 rounded-lg text-white ${
                    item.disabled
                      ? "bg-gray-400 cursor-not-allowed"
                      : item.highlight
                      ? item.highlightColor === "blue"
                        ? "bg-[#5B9BD5] hover:opacity-90"
                        : "bg-[#ED7D31] hover:opacity-90"
                      : "bg-purple-600 hover:bg-purple-700"
                  }`}
                  onClick={item.action}
                  disabled={item.disabled}
                >
                  {item.button}
                </button>
              </div>
            ))}
          </div>

          {goalPlannings.length > 0 && (
            <div className="mt-6 bg-gray-100 p-4 rounded-lg shadow-md mb-6">
              <h3 className="text-md font-semibold mb-4">My Goal Planning</h3>
              <table className="w-full border-collapse border bg-white">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border p-2 text-left">Year</th>
                    <th className="border p-2 text-left">Status</th>
                    <th className="border p-2 text-left">Submitted On</th>
                    <th className="border p-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {goalPlannings.map((gp) => (
                    <tr key={gp.id} className="hover:bg-gray-50">
                      <td className="border p-2">{gp.year}</td>
                      <td className="border p-2">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${getGoalPlanningStatusClass(
                            gp.status
                          )}`}
                        >
                          {getGoalPlanningStatusLabel(gp.status, gp.reinitiatedAt)}
                        </span>
                        {gp.status === "initiated" && gp.reinitiatedAt && (
                          <span className="ml-2 text-[10px] font-semibold text-[#C65911]">
                            Reinitiated by Super Admin
                          </span>
                        )}
                      </td>
                      <td className="border p-2">
                        {gp.status === "initiated"
                          ? "Not yet submitted"
                          : gp.updatedAt
                          ? new Date(gp.updatedAt).toLocaleDateString("en-GB")
                          : "N/A"}
                      </td>
                      <td className="border p-2">
                        {gp.status === "initiated" ? (
                          <button
                            type="button"
                            onClick={handleOpenGoalPlanningForm}
                            className="px-3 py-1 bg-[#ED7D31] text-white rounded text-xs hover:opacity-90"
                          >
                            Continue Form
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleViewGoalPlanning(gp)}
                            className="px-3 py-1 bg-[#241763] text-white rounded text-xs hover:bg-[#1a1047]"
                          >
                            View Submitted Form
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {adminCheckIns.length > 0 && (
            <div className="mt-6 bg-gray-100 p-4 rounded-lg shadow-md mb-6">
              <h3 className="text-md font-semibold mb-4">My Check-ins</h3>
              <table className="w-full border-collapse border bg-white">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border p-2 text-left">Form</th>
                    <th className="border p-2 text-left">Year</th>
                    <th className="border p-2 text-left">Status</th>
                    <th className="border p-2 text-left">Submitted On</th>
                    <th className="border p-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {adminCheckIns.map((checkIn) => (
                    <tr key={checkIn.id} className="hover:bg-gray-50">
                      <td className="border p-2">{getCheckInLabel(checkIn.checkInType)}</td>
                      <td className="border p-2">{checkIn.year}</td>
                      <td className="border p-2">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${getGoalPlanningStatusClass(
                            checkIn.status
                          )}`}
                        >
                          {getGoalPlanningStatusLabel(
                            checkIn.status,
                            checkIn.reinitiatedAt
                          )}
                        </span>
                        {checkIn.status === "initiated" && checkIn.reinitiatedAt && (
                          <span className="ml-2 text-[10px] font-semibold text-[#C65911]">
                            Reinitiated by Super Admin
                          </span>
                        )}
                      </td>
                      <td className="border p-2">
                        {checkIn.status === "initiated"
                          ? "Not yet submitted"
                          : checkIn.updatedAt
                          ? new Date(checkIn.updatedAt).toLocaleDateString("en-GB")
                          : "N/A"}
                      </td>
                      <td className="border p-2">
                        {checkIn.status === "initiated" ? (
                          <button
                            type="button"
                            onClick={() => handleOpenCheckInForm(checkIn.checkInType)}
                            className="px-3 py-1 bg-[#5B9BD5] text-white rounded text-xs hover:opacity-90"
                          >
                            Continue Form
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleViewCheckIn(checkIn)}
                            className="px-3 py-1 bg-[#241763] text-white rounded text-xs hover:bg-[#1a1047]"
                          >
                            View Submitted Form
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {siteVisits.length > 0 && (
            <div className="mt-6 bg-gray-100 p-4 rounded-lg shadow-md mb-6">
              <h3 className="text-md font-semibold mb-4">My Site Visits</h3>
              <table className="w-full border-collapse border bg-white">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border p-2 text-left">Visit #</th>
                    <th className="border p-2 text-left">Year</th>
                    <th className="border p-2 text-left">Status</th>
                    <th className="border p-2 text-left">Submitted On</th>
                    <th className="border p-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {siteVisits.map((siteVisit) => (
                    <tr key={siteVisit.id} className="hover:bg-gray-50">
                      <td className="border p-2">{siteVisit.visitNumber || "N/A"}</td>
                      <td className="border p-2">{siteVisit.year}</td>
                      <td className="border p-2">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${getSiteVisitStatusClass(
                            siteVisit.status
                          )}`}
                        >
                          {getSiteVisitStatusLabel(siteVisit.status)}
                        </span>
                      </td>
                      <td className="border p-2">
                        {siteVisit.status === "initiated" ||
                        siteVisit.status === "observations_captured"
                          ? "Not yet submitted"
                          : siteVisit.updatedAt
                          ? new Date(siteVisit.updatedAt).toLocaleDateString("en-GB")
                          : "N/A"}
                      </td>
                      <td className="border p-2">
                        {siteVisit.status === "initiated" ||
                        siteVisit.status === "observations_captured" ? (
                          <button
                            type="button"
                            onClick={() => handleOpenSiteVisitForm(siteVisit)}
                            className="px-3 py-1 bg-[#4472C4] text-white rounded text-xs hover:opacity-90"
                          >
                            {siteVisit.status === "observations_captured"
                              ? "Complete Post-Observation"
                              : "Complete Pre-Observation"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleViewSiteVisit(siteVisit)}
                            className="px-3 py-1 bg-[#241763] text-white rounded text-xs hover:bg-[#1a1047]"
                          >
                            View Submitted Form
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            {leaveSummary.map((item, index) => (
              <div key={index} className="bg-gray-100 p-4 rounded-lg shadow-md">
                <div className="flex justify-between items-center">
                  <h3 className="text-md font-semibold flex items-center">
                    <span className="mr-2">{item.emoji}</span> {item.title}
                  </h3>
                  <a href="#" className="text-blue-500 text-sm">View Detail</a>
                </div>
                <p className="text-gray-500 text-sm">{item.used} Used / Out of {item.total}</p>
                <div className="w-full bg-gray-200 h-2 rounded-lg mt-2">
                  <div className="bg-purple-600 h-2 rounded-lg" style={{ width: item.total > 0 ? `${(item.used / item.total) * 100}%` : "0%" }}></div>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-gray-100 p-4 rounded-lg shadow-md mb-6">
            <h3 className="text-md font-semibold mb-4">Total Assigned Leaves</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: "Casual Leave", value: totalAssignedLeaves.casualLeave },
                { label: "Sick Leave", value: totalAssignedLeaves.sickLeave },
                { label: "Annual Leave", value: totalAssignedLeaves.annualLeave },
              ].map((item, index) => (
                <div key={index} className="bg-white p-3 rounded-lg shadow-inner">
                  <h4 className="text-sm font-medium text-gray-700">{item.label}</h4>
                  <p className="text-lg font-semibold">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 bg-gray-100 p-4 rounded-lg shadow-md">
            <h3 className="text-md font-semibold mb-4">My Sessions</h3>
            {sessionsStatus === "loading" ? (
              <div className="text-center py-4">
                <p className="text-gray-500">Loading sessions...</p>
              </div>
            ) : teacherSessions[adminEmail] && teacherSessions[adminEmail].length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {teacherSessions[adminEmail].map((session) => (
                  <div key={session.id} className="bg-white p-4 rounded-lg shadow-md">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-lg">{session.sessionName}</h4>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        session.status === "Running" 
                          ? "bg-green-100 text-green-800" 
                          : session.status === "Completed"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-orange-100 text-orange-800"
                      }`}>
                        {session.status}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p><span className="font-medium">Start:</span> {session.start ? new Date(session.start).toLocaleDateString("en-GB") : "N/A"}</p>
                      <p><span className="font-medium">End:</span> {session.end ? new Date(session.end).toLocaleDateString("en-GB") : "N/A"}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-4 text-gray-500">No sessions assigned yet.</p>
            )}
          </div>
          <div className="mt-6 bg-gray-100 p-4 rounded-lg shadow-md">
            <h3 className="text-md font-semibold mb-4">Attendance History</h3>
            <table className="w-full border-collapse border bg-white">
              <thead>
                <tr className="bg-gray-200">
                  <th className="border p-2 text-left">Date</th>
                  <th className="border p-2 text-left">Check In</th>
                  <th className="border p-2 text-left">Check Out</th>
                  <th className="border p-2 text-left">Breaks</th>
                  <th className="border p-2 text-left">Total Hours</th>
                  <th className="border p-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {attendanceRecords
                  .filter((record) => record.teacherEmail === adminEmail || record.adminEmail === adminEmail)
                  .map((record) => {
                  const breaks = record.breaks ? JSON.parse(record.breaks) : [];
                  const breakText = breaks.map((b, i) => `Break ${i + 1}: ${b.breakIn || "N/A"} - ${b.breakOut || "N/A"}`).join(", ");
                  return (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="border p-2">{new Date(record.date).toLocaleDateString("en-GB")}</td>
                      <td className="border p-2">{record.checkIn || "N/A"}</td>
                      <td className="border p-2">{record.checkOut || "N/A"}</td>
                      <td className="border p-2">{breakText || "No breaks"}</td>
                      <td className="border p-2">{record.totalHours || "N/A"}</td>
                      <td className="border p-2">{record.status}</td>
                    </tr>
                  );
                })}
                {attendanceRecords.filter((record) => record.teacherEmail === adminEmail || record.adminEmail === adminEmail).length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center p-4 text-gray-500">No attendance records yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-6 bg-gray-100 p-4 rounded-lg shadow-md">
            <h3 className="text-md font-semibold mb-4">Applied Leaves</h3>
            <table className="w-full border-collapse border bg-white">
              <thead>
                <tr className="bg-gray-200">
                  <th className="border p-2 text-left">Leave Type</th>
                  <th className="border p-2 text-left">Date From</th>
                  <th className="border p-2 text-left">Date To</th>
                  <th className="border p-2 text-left">Reason</th>
                  <th className="border p-2 text-left">Status</th>
                  <th className="border p-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leaves
                  .filter((leave) => leave.adminEmail === adminEmail || leave.teacherEmail === adminEmail)
                  .map((leave) => (
                    <tr key={leave.id} className="hover:bg-gray-50">
                      <td className="border p-2 capitalize">{leave.leaveType.replace(/([A-Z])/g, " $1").trim()}</td>
                      <td className="border p-2">{new Date(leave.dateFrom).toLocaleDateString("en-GB")}</td>
                      <td className="border p-2">{leave.dateTo ? new Date(leave.dateTo).toLocaleDateString("en-GB") : "N/A"}</td>
                      <td className="border p-2">{leave.reason || "N/A"}</td>
                      <td className="border p-2 capitalize">{leave.status}</td>
                      <td className="border p-2 flex space-x-2">
                        <button className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group" onClick={() => handleEditLeave(leave)}>
                          <span className="text-[#241763] group-hover:text-white">Edit</span>
                        </button>
                        <button className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group" onClick={() => handleDeleteLeave(leave.id)}>
                          <span className="text-[#241763] group-hover:text-white">Delete</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                {leaves.filter((leave) => leave.adminEmail === adminEmail || leave.teacherEmail === adminEmail).length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center p-4 text-gray-500">No leaves applied yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <ApplyLeaveModal
          isModalOpen={isApplyLeaveModalOpen}
          isEdit={isEdit}
          onSubmit={onApplyLeaveSubmit}
          handleSubmit={handleSubmit}
          setIsModalOpen={setIsApplyLeaveModalOpen}
          register={register}
          reset={reset}
          isMultipleDays={isMultipleDays}
          setIsMultipleDays={setIsMultipleDays}
        />
        <AttendanceModal
          isOpen={isAttendanceModalOpen}
          onClose={() => setIsAttendanceModalOpen(false)}
          onSubmit={onMarkAttendanceSubmit}
          handleSubmit={handleSubmit}
          register={register}
          reset={reset}
        />
        <WarningModal
          isOpen={isWarningModalOpen}
          onClose={() => setIsWarningModalOpen(false)}
          userEmail={adminEmail}
          isAdmin={true}
        />
        <GoalPlanningModal
          isOpen={isGoalPlanningModalOpen}
          onClose={handleCloseGoalPlanningModal}
          mode={goalPlanningModalMode}
          adminName={adminName}
          goalPlanning={
            goalPlanningModalMode === "admin"
              ? activeGoalPlanning
              : selectedGoalPlanning
          }
          onAdminDraftSave={handleAdminGoalPlanningDraftSave}
          onAdminSubmit={handleAdminGoalPlanningSubmit}
          loading={goalPlanningLoading}
        />
        <AdminCheckInModal
          isOpen={isCheckInModalOpen}
          onClose={handleCloseCheckInModal}
          mode={checkInModalMode}
          checkInType={selectedCheckInType}
          adminName={adminName}
          checkIn={
            checkInModalMode === "admin"
              ? selectedCheckInType === "mid_year"
                ? activeMidYearCheckIn
                : activeEndOfYearCheckIn
              : selectedCheckIn
          }
          onAdminDraftSave={handleAdminCheckInDraftSave}
          onAdminSubmit={handleAdminCheckInSubmit}
          loading={checkInLoading}
        />
        <SiteVisitModal
          isOpen={isSiteVisitModalOpen}
          onClose={handleCloseSiteVisitModal}
          mode={siteVisitModalMode}
          adminName={adminName}
          siteVisit={
            siteVisitModalMode === "admin"
              ? selectedSiteVisit || activeSiteVisit
              : selectedSiteVisit
          }
          onAdminSubmit={handleAdminSiteVisitSubmit}
          onAdminDraftSave={handleAdminSiteVisitDraftSave}
          loading={siteVisitLoading}
        />
        <Toaster />
      </div>
    </div>
  );
};

export default AdminDashboardModal;